import { MetaData, ServiceLineInfo } from './claims.model';

import {
  Attachment,
  ServiceLineDetails,
  ReconsiderationType,
} from '@cigna/chcp/shared/util';

export interface UserBase {
  id: string;
  firstName: string;
  lastName: string;
}

export enum AdjustmentStatus {
  ADJUSTED = 'ADJUSTED',
  CLOSED = 'CLOSED',
  CLOSED_REROUTED_APPEAL = 'CLOSED_REROUTED_APPEAL',
  CLOSED_REROUTED_RECON = 'CLOSED_REROUTED_RECON',
  DRAFT = 'DRAFT',
  IN_PROCESS = 'IN_PROCESS',
  OVERTURNED = 'OVERTURNED',
  OVERTURN_REVERTED = 'OVERTURN_REVERTED',
  PARTIAL_OVERTURN = 'PARTIAL_OVERTURN',
  PENDED = 'PENDED',
  RECEIVED = 'RECEIVED',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  SCANNER_FAILED = 'SCANNER_FAILED',
  SCANNER_PENDING = 'SCANNER_PENDING',
  UNAVAILABLE = 'UNAVAILABLE',
  UPHELD_INSUFFICIENT_INFO = 'UPHELD_INSUFFICIENT_INFO',
  UPHELD_REVERTED = 'UPHELD_REVERTED',
  UPHELD_SUCCESS = 'UPHELD_SUCCESS',
}

export const activeAdjustmentStatuses: AdjustmentStatusType[] = [
  AdjustmentStatus.DRAFT,
  AdjustmentStatus.UNAVAILABLE,
  AdjustmentStatus.IN_PROCESS,
  AdjustmentStatus.RECEIVED,
  AdjustmentStatus.PENDED,
  AdjustmentStatus.SCANNER_PENDING,
];

export enum AdjustmentDisplayStatus {
  // internal statuses
  DRAFT = 'Draft',
  SCANNER_PENDING = 'Pending Virus Scan',
  SCANNER_FAILED = 'Request Failed - Virus Found',
  UNAVAILABLE = 'Status Unavailable - Check Back Later',
  // OneView statuses
  RECEIVED = 'Received',
  ADJUSTED = 'Adjusted',
  IN_PROCESS = 'In Process',
  UPHELD_SUCCESS = 'Claim Processing Upheld - Claim Processed Correctly',
  UPHELD_INSUFFICIENT_INFO = 'Claim Processing Upheld - Insufficient Information Provided',
  PENDED = 'Pended',
  PROCESSING_ERROR = 'Request failed – Help desk notified – Please resubmit in 48 hours',
  CLOSED = 'Closed',
  CLOSED_REROUTED_RECON = 'Closed. Re-routed to Reconsiderations',
  CLOSED_REROUTED_APPEAL = 'Closed. Re-routed to Appeals',
  REVIEW_REQUIRED = 'Additional review required. Resolution response to follow',
}

// TODO: When removing {{ ftr_chcp_cloud9_team_web_reconsideration_clarify_ai_update }} make sure to replace AdjustmentDisplayStatus with AdjustmentDisplayStatusNEW
export enum AdjustmentDisplayStatusNEW {
  // internal statuses
  DRAFT = 'Draft',
  SCANNER_PENDING = 'Pending Virus Scan',
  SCANNER_FAILED = 'Request Failed - Virus Found',
  UNAVAILABLE = 'Status Unavailable - Check Back Later',
  // OneView statuses
  RECEIVED = 'Received',
  ADJUSTED = 'Adjusted',
  IN_PROCESS = 'In Process',
  UPHELD_SUCCESS = 'Claim Processing Upheld - Claim Processed Correctly',
  UPHELD_INSUFFICIENT_INFO = 'Claim Processing Upheld - Insufficient Information Provided',
  PENDED = 'Pended',
  PROCESSING_ERROR = 'Request failed – Help desk notified – Please resubmit in 48 hours',
  CLOSED = 'Closed',
  CLOSED_REROUTED_RECON = 'Closed. Re-routed to Reconsiderations',
  REROUTED_TO_CORR = 'In Progress – additional review',
  CLOSED_REROUTED_APPEAL = 'Closed. Re-routed to Appeals',
  REVIEW_REQUIRED = 'Additional review required. Resolution response to follow',
  // new ones
  PARTIAL_OVERTURN = 'Partially Overturned',
  // UPHELD_SUCCESS ='Review  Upheld'
  OVERTURNED = 'Overturned',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  UPHELD_REVERTED = 'Overturned',
  OVERTURN_REVERTED = 'Upheld',
}

export const decisionNotesDowncoding: { [key: string]: string } = {
  RECEIVED:
    'Your reconsideration decision letter is currently in process and will be mailed to you soon.',
  OVERTURNED:
    'The review team looked at everything and decided to change the original decision. A payment adjustment will be applied to the claim and reflected on an updated remittance report.',
  UPHELD_SUCCESS:
    'After reviewing the submitted medical records, we agree with our original decision.',
  PARTIAL_OVERTURN:
    'Our team changed part of the original decision, but some parts stayed the same. If your claim had more than one date of service, not all procedure codes were updated during the review.',
};

export interface ClaimsAdjustmentsResponse {
  metadata: MetaData;
  adjustmentsHistory: Adjustment[];
}

export type AdjustmentStatusType = keyof typeof AdjustmentStatus;

export type AppealPathType = 'Participant' | 'Provider';

export interface Adjustment {
  appealsReason?: string;
  attachments: string[];
  attachmentsData: Attachment[];
  attachmentsRequired?: boolean;
  choiceOne: number | null;
  choiceThree: number | null;
  choiceTwo: number | null;
  claimDos: string;
  claimReferenceNumber?: string;
  confirmationAnswer?: string;
  confirmationQuestion?: string;
  contactInformation: ContactInfoAdjustment;
  coverageType: string;
  createDate?: string;
  createdBy?: UserBase;
  decisionDate?: string;
  decisionNotes?: string;
  expirationTime: number | null;
  extRefNum: string;
  groupNumber: string;
  householdSuffix: string;
  id: string;
  isDowncodingAdjustment?: boolean;
  memberDob: string;
  memberFirstName: string;
  memberId: string;
  memberLastName: string;
  nonTimelyAttachmentsRequired?: boolean;
  nonTimelyChoice: number | null;
  nonTimelyNotesRequired?: boolean;
  notes: string;
  notesRequired?: boolean;
  oneViewRequestType?: string;
  placeOfService?: string;
  precertId: string;
  procedureLines?: ServiceLineInfo[];
  processStateUrl: string;
  providerId: string;
  providerName: string;
  providerRenderingState?: string;
  reconsiderationType?: ReconsiderationType | '';
  requesterName: string;
  requesterSSOId: string;
  requestType?: string;
  reviewType: ReviewType;
  serviceLines?: ServiceLineDetails[];
  sourceSystemCode: string;
  stateOfService: string;
  status: AdjustmentStatusType;
  submittedBy?: UserBase;
  submittedDate?: string;
  tinNumber?: string;
  typeOfInitiator?: AppealPathType;
  typeOfNetwork: string;
  updateDate?: string;
  updatedBy?: UserBase;
}

export interface MemberGroupSummary {
  idCardExtension: string;
  groupNumber: string;
  ami: string;
}

export interface MemberGroupRequest {
  memberFirstName: string;
  relationshipCode: string;
  memberLastName: string;
  memberDateOfBirth: string;
  memberId: string;
  coverageType: string;
  eligibilityAsOfDate: string;
  memberIdentifier: string;
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}
export interface ContactInfoAdjustment extends ContactInfo {
  phone: string;
}

export interface ContactInfoFormData extends ContactInfo {
  phone: PhoneData;
}

export interface PhoneData {
  phone1: string;
  phone2: string;
  phone3: string;
}

export type ReviewType = 'Pre-Service' | 'Post-Service';
